import { KeyboardEvent, useRef, useState } from "react";
import { ArrowUp, Square } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { ConnectorsPopover } from "~/pages/manifold-nextgen/components/conversations/connectors/ConnectorsPopover";
import type { McpGuidelines } from "~/pages/manifold-nextgen/components/conversations/connectors/useMcpGuidelines";
import type { ContextBuffer } from "~/pages/manifold-nextgen/components/conversations/contextBuffer";
import {
  SkillEditorMode,
  type SkillFormData,
  type SkillFormErrors,
} from "~/pages/manifold-nextgen/components/conversations/skills/SkillEditorForm";
import { SkillEditorModal } from "~/pages/manifold-nextgen/components/conversations/skills/SkillEditorModal";
import { SkillsList } from "~/pages/manifold-nextgen/components/conversations/skills/SkillsList";
import { SkillsPopover } from "~/pages/manifold-nextgen/components/conversations/skills/SkillsPopover";
import { useSkillMutations } from "~/pages/manifold-nextgen/components/conversations/skills/useSkillMutations";
import type { AgentConnector } from "~/services/agents";
import { PartialSkill } from "~/services/skills";
import { zendeskArticleUrl } from "~/utils/zendesk";
import { ContextBufferMeter } from "./ContextBufferMeter";
import { Mention, MentionInput, type MentionInputRef } from "./MentionInput";

const SKILLS_HELP_URL = zendeskArticleUrl("43648526871437-AI-Skills-Guide");

interface ComposerProps {
  // Inert composer — used by the loading skeleton on conversation mount.
  disabled?: boolean;
  // True while a run is streaming. Swaps the Send button for a Stop button
  // that calls `onCancel` instead of submitting.
  isRunning?: boolean;
  // True between the moment the user clicks Stop and the moment the agent
  // emits RUN_FINISHED. Disables the button to prevent duplicate cancels.
  isCancelling?: boolean;
  onCancel?: () => void;
  onSubmit: (text: string) => void;
  placeholder?: string;
  // Skills available in this conversation's context. Empty list during
  // loading or when no skills apply is rendered as a friendly empty state.
  skills?: PartialSkill[];
  // Connectors granted to this conversation's agent, the names currently
  // enabled, and a toggle callback. Selection is owned and persisted upstream.
  connectors?: AgentConnector[];
  enabledConnectorNames?: string[];
  onToggleConnector?: (connectorName: string) => void;
  // MCP-guidelines acceptance gate for the connectors picker.
  mcpGuidelines?: McpGuidelines;
  // Context-window token usage for the current run, or null before any tokens
  // have been sent — in which case the usage meter is hidden.
  contextBuffer?: ContextBuffer | null;
}

export const Composer = ({
  disabled,
  isRunning,
  isCancelling,
  onCancel,
  onSubmit,
  placeholder = "What would you like to know?",
  skills = [],
  connectors = [],
  enabledConnectorNames = [],
  onToggleConnector,
  mcpGuidelines,
  contextBuffer,
}: ComposerProps) => {
  const inputRef = useRef<MentionInputRef>(null);
  const [text, setText] = useState("");

  const { createUserSkill, updateSkill, deleteSkill, getSkill } = useSkillMutations();

  // Skill editor modal state.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSkillId, setEditingSkillId] = useState<string | null>(null);
  const [editingSkillData, setEditingSkillData] = useState<Partial<SkillFormData> | undefined>();
  const [editorErrors, setEditorErrors] = useState<SkillFormErrors>({});
  const [savingSkill, setSavingSkill] = useState(false);
  const [loadingSkillContent, setLoadingSkillContent] = useState(false);

  // Insert a "/skill" token from the toolbar picker at the end of the input.
  const insertSkillToken = (token: string) => {
    setText((current) => {
      const needsSpace = current.length > 0 && !current.endsWith(" ") && !current.endsWith("\n");
      return `${current}${needsSpace ? " " : ""}${token} `;
    });
    setTimeout(() => inputRef.current?.focusAtEnd(), 0);
  };

  const openSkillsHelp = () => window.open(SKILLS_HELP_URL, "_blank", "noopener,noreferrer");

  const handleAddSkill = () => {
    setEditingSkillId(null);
    setEditingSkillData(undefined);
    setEditorErrors({});
    setEditorOpen(true);
  };

  const handleEditSkill = async (skill: PartialSkill) => {
    setEditingSkillId(skill.id);
    setEditingSkillData(undefined);
    setEditorErrors({});
    setEditorOpen(true);
    setLoadingSkillContent(true);
    try {
      const full = await getSkill(skill.id);
      setEditingSkillData({
        name: full.name,
        description: full.description ?? "",
        content: full.content,
      });
    } catch (error) {
      console.error("Failed to load skill:", error);
      setEditorErrors({ general: "Failed to load skill content" });
    } finally {
      setLoadingSkillContent(false);
    }
  };

  const handleDeleteSkill = async (skill: PartialSkill) => {
    try {
      await deleteSkill(skill);
    } catch (error) {
      console.error("Failed to delete skill:", error);
    }
  };

  const handleSaveSkill = async (data: SkillFormData) => {
    setSavingSkill(true);
    setEditorErrors({});
    try {
      if (editingSkillId) {
        await updateSkill(editingSkillId, data);
      } else {
        await createUserSkill(data);
      }
      setEditorOpen(false);
      setEditingSkillId(null);
      setEditingSkillData(undefined);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save skill";
      setEditorErrors({ general: message });
    } finally {
      setSavingSkill(false);
    }
  };

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled || isRunning) return;
    setText("");
    onSubmit(trimmed);
  };

  // MentionInput swallows Enter while its suggestion dropdown is open, so this
  // only fires for a plain Enter that should send the message.
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const hasText = text.trim().length > 0;
  const canSubmit = hasText && !disabled && !isRunning;
  const canCancel = Boolean(isRunning && onCancel && !isCancelling);
  // While running, the action button stops the run; otherwise it sends.
  const buttonActive = isRunning ? canCancel : canSubmit;
  const buttonLabel = isRunning ? (isCancelling ? "Cancelling…" : "Stop generation") : "Send";

  return (
    <>
      <div
        className="mx-auto w-full max-w-[60rem] cursor-text rounded-2xl border border-border bg-background"
        onMouseDown={(e) => {
          // Focus the editor when the user clicks anywhere in the shell that
          // isn't a button or the editor itself; preserves native button clicks.
          if ((e.target as HTMLElement).closest("button, [contenteditable]")) return;
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <div className="px-4 pt-3">
          <MentionInput
            ref={inputRef}
            value={text}
            onChange={setText}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            ariaLabel="Message"
            maxHeight={256}
            autoFocus
            disabled={disabled}
          >
            <Mention
              trigger="/"
              type="skill"
              pattern={/\/[\w-]+/g}
              data={skills}
              displayTransform={(skill) => (skill as PartialSkill).name}
              renderSuggestions={(props) => (
                <div
                  style={{
                    position: "fixed",
                    top: props.position.top - 8,
                    left: props.position.left,
                    transform: "translateY(-100%)",
                    zIndex: 50,
                  }}
                  className="w-96 overflow-hidden rounded-lg border border-border bg-popover shadow-md"
                >
                  <SkillsList
                    skills={skills}
                    searchFilter={props.searchText}
                    onSelectSkill={(name) => props.onSelect(`/${name}`)}
                    onAddSkill={() => {
                      handleAddSkill();
                      props.onClose();
                    }}
                    onEditSkill={(skill) => {
                      void handleEditSkill(skill);
                      props.onClose();
                    }}
                    onDeleteSkill={handleDeleteSkill}
                    onLearnMore={openSkillsHelp}
                    enableKeyboardNav
                    onClose={props.onClose}
                  />
                </div>
              )}
            />
          </MentionInput>
        </div>

        <div className="flex items-center justify-between gap-2 px-2 py-2">
          <div className="flex items-center gap-1">
            {onToggleConnector && mcpGuidelines && (
              <ConnectorsPopover
                connectors={connectors}
                enabledNames={enabledConnectorNames}
                onToggle={onToggleConnector}
                mcpGuidelines={mcpGuidelines}
              />
            )}
            <SkillsPopover
              skills={skills}
              onSelect={insertSkillToken}
              onAddSkill={handleAddSkill}
              onEditSkill={(skill) => void handleEditSkill(skill)}
              onDeleteSkill={handleDeleteSkill}
              onLearnMore={openSkillsHelp}
            />
          </div>
          <div className="flex items-center gap-3">
            {contextBuffer && <ContextBufferMeter buffer={contextBuffer} />}
            <Button
              type="button"
              onClick={isRunning ? onCancel : submit}
              disabled={!buttonActive}
              size="icon"
              variant="ghost"
              aria-label={buttonLabel}
              className={cn(
                "group size-8 rounded-full",
                buttonActive
                  ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                  : "bg-muted text-muted-foreground disabled:pointer-events-auto disabled:cursor-default"
              )}
            >
              {isRunning ? (
                <Square
                  className={cn(
                    "size-3 fill-current",
                    buttonActive && "group-hover:animate-icon-pulse"
                  )}
                />
              ) : (
                <ArrowUp
                  className={cn("size-3.5", buttonActive && "group-hover:animate-icon-pulse")}
                />
              )}
            </Button>
          </div>
        </div>
      </div>

      {editorOpen && (
        <SkillEditorModal
          key={editingSkillId ?? "new"}
          open={editorOpen}
          initialData={editingSkillData}
          onClose={() => {
            setEditorOpen(false);
            setEditingSkillId(null);
            setEditingSkillData(undefined);
            setEditorErrors({});
          }}
          onSubmit={handleSaveSkill}
          loading={savingSkill || loadingSkillContent}
          errors={editorErrors}
          mode={editingSkillId ? SkillEditorMode.EDIT : SkillEditorMode.CREATE}
        />
      )}
    </>
  );
};
